import React from 'react';
import Box from '@mui/material/Box';

// Brand icon set, traced from the Al-Bayan sheet. Every pack is monochrome in
// ONE of the app's four sanctioned icon colours — white, black, green
// (#1F5F4A), gold/yellow (#E6A817) — plus the original two-tone (green+gold)
// art. Each colour comes in a normal and a thickened ("bold") weight, under
// src/assets/icons/:
//
//   bayan/  bayan-bold/               two-tone green #1F5F4A + gold #E6A817
//   bayan-green/  bayan-bold-green/   monochrome green
//   bayan-yellow/ bayan-bold-yellow/  monochrome gold
//   bayan-white/  bayan-bold-white/   monochrome white  (generated from green)
//   bayan-black/  bayan-bold-black/   monochrome black  (generated from green)
//
// The bold weight is the app default: at 18-34px the original hairline traces
// read thin, so every path gets a same-colour stroke that fattens thin
// elements the most (best legibility) without blobbing the solid shapes.
//
// CONTRAST IS THE WHOLE POINT. A baked-colour <img> ignores CSS `color`, so a
// two-tone/green icon vanishes on a dark-green surface (the report cards were
// green-on-green). Callers should NOT hand-pick a colour per surface; instead
// leave `tone="auto"` (the default) and BayanIcon resolves it from the live
// MUI theme: white on the dark theme, green on the light theme — always the
// max-contrast choice on the app's neutral surfaces. For icons sitting on a
// COLOURED surface regardless of theme (a filled green button, a gold pill, a
// gradient hero) pass an explicit contrasting tone, e.g. tone="white" on green
// or tone="black" on gold. `toneForSurface(bg)` encodes that rule.
//
// Icons are imported as asset URLs (not inline SVG) so the artwork stays out
// of the JS bundle and renders as a single <img> node instead of hundreds of
// <path> elements. require.context pulls a whole pack in one shot, so adding a
// new numbered icon needs no code change here.

// Only the green packs are bundled: they supply the icon SHAPES used as CSS
// masks. The paint colour comes from CSS (`--bayan-icon-ink` / `tone`), so one
// shape renders in any of the four palette colours — no per-colour pack needed.
/* eslint-disable no-undef */
const CTX = {
  green: require.context('../assets/icons/bayan-green', false, /\.svg$/),
  'green-bold': require.context('../assets/icons/bayan-bold-green', false, /\.svg$/),
};
/* eslint-enable no-undef */

// Filename `01_open_mushaf.svg` -> registry key `open_mushaf`.
const keyFromFile = (path) => {
  const m = path.match(/\.\/\d+_(.+)\.svg$/);
  return m ? m[1] : null;
};

const buildRegistry = (ctx) => {
  const reg = {};
  ctx.keys().forEach((path) => {
    const key = keyFromFile(path);
    if (!key) return;
    const mod = ctx(path);
    reg[key] = (mod && mod.default) || mod;
  });
  return reg;
};

const PACKS = Object.fromEntries(
  Object.entries(CTX).map(([name, ctx]) => [name, buildRegistry(ctx)]),
);

const SIZE = { small: 18, medium: 24, large: 35, inherit: '1em' };

/**
 * Map a background to the icon tone that reads with maximum contrast on it.
 * Accepts a loose descriptor ('dark' | 'light' | a hex/colour string) and
 * returns 'white' on dark/saturated surfaces, 'green' on light ones. Gold
 * surfaces read best with black ink.
 */
export function toneForSurface(bg) {
  if (!bg) return 'green';
  const s = String(bg).toLowerCase();
  if (s === 'dark' || s === 'primary' || s === 'black') return 'white';
  if (s === 'gold' || s === 'yellow' || s === 'warning' || s === 'secondary') return 'black';
  if (s === 'light' || s === 'white' || s === 'surface' || s === 'grey' || s === 'gray') return 'green';
  const hex = s.replace('#', '');
  if (/^[0-9a-f]{6}$/.test(hex)) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum < 0.5 ? 'white' : 'green';
  }
  return 'green';
}

// The four sanctioned icon colours. `tone` maps to one of these; anything
// else (incl. the default 'auto') paints from the `--bayan-icon-ink` CSS
// variable, which the theme sets to white(dark)/green(light) on neutral
// surfaces and flips to `currentColor` inside filled buttons/chips/FABs/
// selected toggles — so the glyph always contrasts its surface.
const TONE_COLOR = {
  white:  '#FFFFFF',
  black:  '#141414',
  green:  '#1F5F4A',
  yellow: '#E6A817',
};
const AUTO_INK = 'var(--bayan-icon-ink, currentColor)';

/**
 * Drop-in brand icon. Rendered as a CSS mask (the SVG supplies the SHAPE; the
 * colour comes from CSS), so a single glyph paints in whatever palette colour
 * its surface needs — never baked, never green-on-green. Usage:
 *   <BayanIcon name="open_mushaf" fontSize="small" sx={{ mr: 1 }} />
 *   <BayanIcon name="home_dashboard" tone="yellow" />   // force gold accent
 *   <BayanIcon name="search" bold={false} />            // original hairline weight
 *
 * `tone`: 'auto' (default — white on dark, green on light, and the surface's
 *         contrast colour inside filled buttons/chips) | 'white' | 'black' |
 *         'green' | 'yellow'
 * `bold`: true (default, thickened) | false (original traced weight)
 */
export default function BayanIcon({ name, tone = 'auto', bold = true, fontSize = 'medium', size, sx, className, style, onClick, title, id }) {
  // Any pack supplies the shape for the mask (the fill colour is irrelevant —
  // CSS repaints it). Use the bold/regular green pack for weight; fall back to
  // the two-tone art only if a name is missing from the monochrome pack.
  const shapePack = PACKS[bold ? 'green-bold' : 'green'] || PACKS.green;
  const src = shapePack[name] || PACKS.green[name] || PACKS['green-bold']?.[name];
  if (!src) return null;
  const s = size != null ? size : (SIZE[fontSize] || 24);
  const ink = TONE_COLOR[tone] || AUTO_INK;
  return (
    <Box
      component="span"
      id={id}
      title={title}
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
      onClick={onClick}
      className={`bayan-icon${className ? ` ${className}` : ''}`}
      style={style}
      sx={{
        display: 'inline-block',
        width: s,
        height: s,
        flexShrink: 0,
        verticalAlign: 'middle',
        backgroundColor: ink,
        WebkitMaskImage: `url(${src})`,
        maskImage: `url(${src})`,
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        ...sx,
      }}
    />
  );
}

export const BAYAN_ICON_NAMES = Object.keys(PACKS.green);
